<script lang="ts">
	import { page } from '$app/stores';
	import UsersGroup from '$lib/assets/images/icons/users-group.svg?component';
	import ContactFormVerification from '$lib/components/Global/ContactFormVerification.svelte';
	import Step from '$lib/components/Simulations/Components/Stepper/Step.svelte';
	import __ from '$lib/stores/TranslationStore';
	import { convertToCurrency, validateNIF } from '$lib/utils/helpers/numbers';
	import { onMount, tick } from 'svelte';
	import ChoiceField from '../GenericSimulation/Fields/ChoiceField.svelte';
	import DateInput, { formatStandardDate } from '../GenericSimulation/Fields/DateInput.svelte';
	import Input from '../GenericSimulation/Fields/Input.svelte';
	import MoneyInput from '../GenericSimulation/Fields/MoneyInput.svelte';
	import GenericSimulation from '../GenericSimulation/GenericSimulation.svelte';
	import Reminder from '../GenericSimulation/Popups/Reminder.svelte';
	import ContactSubmitted from '../GenericSimulation/Result/ContactSubmitted.svelte';
	import ResultFrame from '../GenericSimulation/Result/ResultFrame.svelte';
	import SimulationCard from '../GenericSimulation/Result/SimulationCard.svelte';
	import TaxCard from '../GenericSimulation/Result/TaxCard.svelte';
	import ContactStep from '../GenericSimulation/Steps/ContactStep.svelte';
	import { type StepperStore } from '../Stepper/stepper';
	import Stepper from '../Stepper/Stepper.svelte';


	let result: Awaited<ReturnType<typeof submitSimulation>> | null = null;
	let resultVerified = false;
	let submitted = false;
	let error = false;

	let contactSubmitted = false;
	let contactDialogOpen = false;

	async function submitSimulation(data: Record<string, unknown>) {
		const response = await fetch('/api/crm/simulations/new-personal-credit', {
			method: 'POST',
			body: JSON.stringify({
				amount: convertToCurrency(data.amount as string),
				term: data.term,
				recieve_communications: data.recieve_communications,
				main_holder: {
					name: data.name,
					email: data.email,
					birthdate: formatStandardDate(data.birthdate as string),
					nif: data.nif,
					phone: data.phone,
					prefix: '+351'
				},
				second_holder:
					data.two_holders === 'yes'
						? {
								name: data.name_two,
								birthdate: formatStandardDate((data.birthdate_two as string) || ''),
								nif: data.nif_two
						  }
						: {}
			})
		});

		const json = await response.json();

		if (!response.ok || !json.success) {
			throw new Error('Error submitting simulation');
		}

		if (window?.gtag) window?.gtag('event', 'Submit', { event_category: 'Sim Cred Pessoal' });

		return json?.data?.data as {
			lead: string;
			name: string;
			simulation: {
				amount: number;
				installments: number;
				term: number;
				tan: number;
			};
		};
	}

	async function handleResults(stepper: StepperStore) {
		if (submitted) return;
		submitted = true;

		simulator.doLoading(async () => {
			result = await submitSimulation(stepper.extractAllData());
             resultVerified = true;
			stepper.showResult();
		});
	}

	async function handleDirectContact(stepper: StepperStore) {
		try {
			const response = await fetch('/api/crm/leads/' + result?.lead + '/direct-contact', {
				method: 'POST'
			});
			const json = await response.json();
			contactDialogOpen = true;
			contactSubmitted = true;
		} catch (error) {
			error = true;
		}
	}

	let timeChoice: ChoiceField;
	let twoHolders: ChoiceField;
	let hasTwoHolders = false;

	function handleRetry(stepper: StepperStore) {
		stepper.reset();
		result = null;
		resultVerified = false;
		submitted = false;
		contactSubmitted = false;
		contactDialogOpen = false;
		error = false;
	}

	let simulator: GenericSimulation;

    let focusOnMount = !$page.url.pathname.includes('/embed/'); //Avoid onfocus due to scrollTo browser action, when is embed in Articles


	let popup = true;
	onMount(() => {});
</script>

<Stepper let:stepper>
	<GenericSimulation
		title={$__('Simulador')}
		caption={$__('Crédito pessoal')}
		icon={UsersGroup}
		bind:this={simulator}
		on:retry={() => handleRetry(stepper)}
		{popup}
	>
		<svelte:fragment slot="popup">
			<Reminder on:close={() => (popup = false)} />
		</svelte:fragment>
		<!-- {@const test = stepper.showResult()} -->
		<Step
			title={$__('Qual o <strong>valor</strong> de que precisa?')}
			validate={{
				amount: (value) => {
					const num = convertToCurrency(value);

					if (num < 5000 || num >= 100000) {
						return $__('O valor deve ser entre 5000€ e 100.000€');
					}
				}
			}}
			required={['amount']}
			displayCaption={(step) => `${step.data.amount}€`}
		>
			<MoneyInput name="amount" focusOnMount={focusOnMount} label={$__('Valor solicitado')} />
		</Step>
		<Step
			title={$__('Em quanto <strong>tempo</strong> quer <strong>pagar</strong>?')}
			actionButton={false}
			displayCaption={(step) => `${step.data.term} ${$__('meses')}`}
		>
			<ChoiceField
				name="term"
				bind:this={timeChoice}
				placeholder={$__('Prazo')}
				on:change={(e) => {
					timeChoice.safeChange(() => {
						if (timeChoice.value != '') stepper.goNextStep();
					});
				}}
				options={[24, 36, 48, 60, 72, 84].map((months) => ({
					label: months + '<br> ' + $__('meses'),
					value: String(months)
				}))}
			/>
		</Step>
		<ContactStep />
		<Step title={$__('É um projeto a <strong>dois titulares</strong>?')} actionButton={false}>
			<div slot="caption" class="flex gap-3">
				<div class="text-[17px]">⚠️</div>
				<h3 class="text-xs">
					{$__('Adicionar um segundo titular aumenta a probabilidade de aprovação do crédito.')}
				</h3>
			</div>
			<ChoiceField
				name="two_holders"
				bind:this={twoHolders}
				variant="grid-only"
				on:change={(e) =>
					twoHolders.safeChange(async () => {
						hasTwoHolders = e.detail === 'yes';
						if (hasTwoHolders) {
							await tick();
							stepper.goNextStep();
							return;
						}
						handleResults(stepper);
					})}
				options={[
					{ label: $__('Sim'), value: 'yes' },
					{ label: $__('Não'), value: 'no' }
				]}
			/>
		</Step>
		{#if hasTwoHolders}
			<Step
				title={$__('Dados pessoais do <strong>2° titular</strong>.')}
				on:complete={() => handleResults(stepper)}
				displayCaption={(step) => `
					${step.data.name_two}
					${step.data.birthdate_two}
					${step.data.nif_two}
				`}
				validate={{
					name_two: (value) => {
						if (!/^(?=.{2,})([A-Za-zÀ-ÖØ-öø-ÿ\-]+(?:\s[A-Za-zÀ-ÖØ-öø-ÿ\-]+)+)$/.test(value)) {
							return $__('Deve colocar, no mínimo, dois nomes.');
						}
					},
					nif_two: (value) => {
						if (!value) return;
						if (!validateNIF(value)) {
							return $__('O NIF introduzido é inválido.');
						}
					}
				}}
				required={['name_two', 'birthdate_two']}
			>
				<div class="flex flex-col gap-6">
					<Input name="name_two" label={$__('Nome e apelido')} />
					<DateInput name="birthdate_two" label={$__('Data de nascimento')} />
					<Input name="nif_two" label="NIF" inputmode="numeric" />
				</div>
			</Step>
		{/if}
		<div slot="content" class="flex flex-col justify-center">
			{#if error}
				<p>Error</p>
				<!-- <div class="-mt-4 h-full w-full">
					<SimulationError on:retry={() => handleRetry(stepper)} />
				</div> -->
			{/if}
			{#if result}
				{@const resultData = result.simulation}
				{#if resultVerified}
					{#if contactDialogOpen}
						<ContactSubmitted on:close={() => (contactDialogOpen = false)} />
					{/if}
					<ResultFrame
						clientName={result.name}
						on:retry={() => handleRetry(stepper)}
						directContactRequested={contactSubmitted}
						on:contact={() => handleDirectContact(stepper)}
					>
						<div class="grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-2 lg:gap-6">
							<TaxCard amount={resultData.installments} title={$__('Taxa fixa')} />
							<SimulationCard
								class="col-span-full md:order-2 md:col-span-1"
								items={[
									{ label: $__('Valor a financiar'), value: `${resultData.amount}€` },
									{ label: $__('TAN desde'), value: `${resultData.tan}%` },
									{ label: $__('Prazo'), value: `${resultData.term} ${$__('meses')}` }
								]}
							/>
						</div>
					</ResultFrame>
				{:else}
					<div class="self-center">
						<ContactFormVerification
							on:success={() => {
								resultVerified = true;
							}}
							verificationId={result?.lead}
						/>
					</div>
				{/if}
			{/if}
		</div>
	</GenericSimulation>
</Stepper>
